import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Swal from 'sweetalert2';
import {
  FiBarChart2,
  FiBookOpen,
  FiClipboard,
  FiDollarSign,
  FiHome,
  FiLogOut,
  FiMapPin,
  FiMenu,
  FiPackage,
  FiSettings,
  FiShoppingBag,
  FiTruck,
  FiUsers,
} from 'react-icons/fi';
import logoPadrao from '../assets/logo.png';
import { atualizarPerfil, logout } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { lerArquivoComoDataUrl } from '../utils/arquivos';
import { formatarCelular } from '../utils/mascaras';
import { Button } from './Button';
import { ImageField } from './ImageField';
import { Input } from './Input';

const LINKS = [
  { to: '/painel', label: 'Inicio', end: true, icon: FiHome },
  { to: '/pedidos', label: 'Pedidos', icon: FiTruck },
  { to: '/cardapio', label: 'Cardapio', icon: FiBookOpen },
  { to: '/materias-primas', label: 'Materia-prima', icon: FiPackage },
  { to: '/produtos', label: 'Produtos', icon: FiShoppingBag },
  { to: '/kds', label: 'Cozinha', icon: FiClipboard },
  { to: '/equipe', label: 'Equipe', icon: FiUsers },
  { to: '/rastreamento', label: 'Rastreamento', icon: FiMapPin },
  { to: '/financeiro', label: 'Financeiro', icon: FiDollarSign },
  { to: '/relatorios', label: 'Relatorios', icon: FiBarChart2 },
];

const LARGURA_SIDEBAR = 252;

function iniciaisDoNome(nome?: string) {
  if (!nome) return '?';
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
  return (primeira + ultima).toUpperCase();
}

function AvatarUsuario({ nome, fotoUrl, className = '' }: { nome?: string; fotoUrl?: string | null; className?: string }) {
  if (fotoUrl) {
    return <img src={fotoUrl} alt={nome || 'Usuario'} className={`lizzi-avatar-image ${className}`.trim()} />;
  }

  return <div className={`lizzi-avatar ${className}`.trim()}>{iniciaisDoNome(nome)}</div>;
}

function LinksNavegacao({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
      {LINKS.map((link) => {
        const Icone = link.icon;
        return (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            onClick={onNavigate}
            style={({ isActive }) => ({
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--cor-branco)',
              textDecoration: 'none',
              fontWeight: isActive ? 700 : 500,
              fontSize: '14px',
              padding: '10px 14px',
              borderRadius: 'var(--raio-padrao)',
              background: isActive ? 'rgba(255,255,255,0.18)' : 'transparent',
              boxShadow: isActive ? 'inset 3px 0 0 #fff' : 'none',
              opacity: isActive ? 1 : 0.85,
              transition: 'background 0.15s ease, opacity 0.15s ease',
            })}
          >
            <span
              style={{
                width: '26px',
                height: '26px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.14)',
                flexShrink: 0,
              }}
            >
              <Icone size={15} />
            </span>
            {link.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { usuario, setUsuario } = useAuth();
  const navigate = useNavigate();
  const [mostrarMenu, setMostrarMenu] = useState(false);
  const [mostrarPerfil, setMostrarPerfil] = useState(false);
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);
  const [perfil, setPerfil] = useState({
    empresa: '',
    nome: '',
    email: '',
    celular: '',
    foto_base64: null as string | null,
    logo_base64: null as string | null,
    remover_foto: false,
    remover_logo: false,
  });

  useEffect(() => {
    setPerfil({
      empresa: usuario?.tenant_nome || '',
      nome: usuario?.nome || '',
      email: usuario?.email || '',
      celular: usuario?.celular || '',
      foto_base64: null,
      logo_base64: null,
      remover_foto: false,
      remover_logo: false,
    });
  }, [usuario]);

  async function handleLogout() {
    await logout();
    setUsuario(null);
    Swal.fire({ icon: 'success', title: 'Ate logo!', timer: 1200, showConfirmButton: false });
    navigate('/login');
  }

  async function handleImagemPerfil(campo: 'foto_base64' | 'logo_base64', file: File | null) {
    if (!file) return;
    try {
      const image = await lerArquivoComoDataUrl(file);
      setPerfil((atual) => ({
        ...atual,
        [campo]: image,
        ...(campo === 'foto_base64' ? { remover_foto: false } : { remover_logo: false }),
      }));
    } catch (error: any) {
      Swal.fire({ icon: 'error', title: 'Nao foi possivel carregar a imagem', text: error.message });
    }
  }

  async function handleSalvarPerfil(event: React.FormEvent) {
    event.preventDefault();
    setSalvandoPerfil(true);

    try {
      const { usuario: usuarioAtualizado } = await atualizarPerfil(perfil);
      setUsuario(usuarioAtualizado);
      setMostrarPerfil(false);
      Swal.fire({ icon: 'success', title: 'Perfil atualizado!', timer: 1200, showConfirmButton: false });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel salvar',
        text: error.response?.data?.erro || 'Confira os dados e tente novamente.',
      });
    } finally {
      setSalvandoPerfil(false);
    }
  }

  const logoEmpresa = usuario?.tenant_logo_url || logoPadrao;

  const conteudoSidebar = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--cor-gradiente)',
        color: 'var(--cor-branco)',
        padding: '20px 14px',
      }}
    >
      <div className="lizzi-sidebar-brand">
        <img src={logoEmpresa} alt={usuario?.tenant_nome || 'Lizzi Food'} width={40} height={40} />
        <div>
          <strong>{usuario?.tenant_nome || 'Lizzi Food'}</strong>
          <span>Lizzi Food</span>
        </div>
      </div>

      <LinksNavegacao onNavigate={() => setMostrarMenu(false)} />

      <div className="lizzi-sidebar-profile-card">
        <div className="lizzi-sidebar-profile-row">
          <AvatarUsuario nome={usuario?.nome} fotoUrl={usuario?.foto_url} className="lizzi-sidebar-avatar" />
          <div>
            <strong>{usuario?.nome || 'Usuario'}</strong>
            <span>{usuario?.tenant_nome || 'Sua empresa'}</span>
          </div>
        </div>
      </div>

      <p style={{ fontSize: '11px', opacity: 0.55, textAlign: 'center', margin: 0, paddingTop: '16px' }}>
        Lizzi Food © {new Date().getFullYear()}
      </p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh' }}>
      <aside
        className="d-none d-lg-block"
        style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: LARGURA_SIDEBAR, zIndex: 10 }}
      >
        {conteudoSidebar}
      </aside>

      <header
        className="d-lg-none"
        style={{
          background: 'var(--cor-gradiente)',
          color: 'var(--cor-branco)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <button
          onClick={() => setMostrarMenu(true)}
          style={{ background: 'transparent', border: 'none', color: 'var(--cor-branco)', padding: 0, cursor: 'pointer' }}
          aria-label="Abrir menu"
        >
          <FiMenu size={24} />
        </button>
        <img src={logoEmpresa} alt={usuario?.tenant_nome || 'Lizzi Food'} width={28} height={28} style={{ borderRadius: '7px', objectFit: 'cover' }} />
        <strong style={{ fontSize: '16px' }}>{usuario?.tenant_nome || 'Lizzi Food'}</strong>
      </header>

      <Offcanvas show={mostrarMenu} onHide={() => setMostrarMenu(false)} placement="start" className="d-lg-none">
        <Offcanvas.Body style={{ padding: 0 }}>{conteudoSidebar}</Offcanvas.Body>
      </Offcanvas>

      <main className="lizzi-conteudo-principal">
        <div className="lizzi-topbar">
          <button type="button" className="lizzi-topbar-profile" onClick={() => setMostrarPerfil(true)}>
            <AvatarUsuario nome={usuario?.nome} fotoUrl={usuario?.foto_url} />
            <div className="lizzi-topbar-profile-copy">
              <strong>{usuario?.nome}</strong>
              <span>{usuario?.tenant_nome || 'Sua empresa'}</span>
            </div>
          </button>

          <div className="lizzi-topbar-actions">
            <div className="lizzi-topbar-company-badge">
              <img src={logoEmpresa} alt={usuario?.tenant_nome || 'Empresa'} />
              <span>{usuario?.tenant_nome || 'Empresa'}</span>
            </div>
            <Button type="button" variant="outline" onClick={() => setMostrarPerfil(true)} fullWidth={false} size="sm" className="lizzi-topbar-profile-button">
              <FiSettings size={15} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
              Meu perfil
            </Button>
            <button className="lizzi-botao-sair" onClick={handleLogout}>
              <FiLogOut size={15} />
              Sair
            </button>
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          <div style={{ maxWidth: '1160px', margin: '0 auto' }}>{children}</div>
        </div>
      </main>

      <Modal show={mostrarPerfil} onHide={() => setMostrarPerfil(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar perfil</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSalvarPerfil} className="lizzi-profile-form">
            <div className="lizzi-profile-images">
              <ImageField
                id="foto-usuario"
                label="Foto do usuario"
                previewUrl={perfil.foto_base64 || (perfil.remover_foto ? null : usuario?.foto_url)}
                onChange={(file) => handleImagemPerfil('foto_base64', file)}
                onRemove={() => setPerfil((atual) => ({ ...atual, foto_base64: null, remover_foto: true }))}
                helperText="Mostrada no topo do sistema"
                variant="avatar"
              />

              <ImageField
                id="logo-empresa-perfil"
                label="Logo da empresa"
                previewUrl={perfil.logo_base64 || (perfil.remover_logo ? null : usuario?.tenant_logo_url)}
                onChange={(file) => handleImagemPerfil('logo_base64', file)}
                onRemove={() => setPerfil((atual) => ({ ...atual, logo_base64: null, remover_logo: true }))}
                helperText="Usada no menu e no cardapio"
                variant="wide"
              />
            </div>

            <div className="lizzi-profile-grid">
              <Input
                label="Empresa"
                value={perfil.empresa}
                onChange={(e) => setPerfil((atual) => ({ ...atual, empresa: e.target.value }))}
                required
              />
              <Input
                label="Nome"
                value={perfil.nome}
                onChange={(e) => setPerfil((atual) => ({ ...atual, nome: e.target.value }))}
                required
              />
              <Input
                label="E-mail"
                type="email"
                value={perfil.email}
                onChange={(e) => setPerfil((atual) => ({ ...atual, email: e.target.value }))}
                required
              />
              <Input
                label="Celular"
                value={perfil.celular}
                onChange={(e) => setPerfil((atual) => ({ ...atual, celular: formatarCelular(e.target.value) }))}
                maxLength={15}
              />
            </div>

            <div className="lizzi-modal-actions">
              <Button type="button" variant="outline" onClick={() => setMostrarPerfil(false)} fullWidth={false}>
                Cancelar
              </Button>
              <Button type="submit" disabled={salvandoPerfil} fullWidth={false}>
                {salvandoPerfil ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
